import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import supabase from '../../../config/supabaseClient';
import toast from 'react-hot-toast';

const PostesIluminacaoContext = createContext();

export const usePostesIluminacao = () => {
  const context = useContext(PostesIluminacaoContext);
  if (!context) {
    throw new Error('usePostesIluminacao deve ser usado dentro de PostesIluminacaoProvider');
  }
  return context;
};

export default function PostesIluminacaoProvider({ children }) {
  const [postes, setPostes] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [filtros, setFiltros] = useState({
    status: '',
    localidade: '',
    tipoLampada: '',
    busca: ''
  });

  // Carregar localidades (bairros)
  const carregarLocalidades = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('ccbairros')
        .select('id, descricao')
        .order('descricao');

      if (error) throw error;
      
      // Mapear os dados para manter compatibilidade com o formato anterior
      const localidadesMapeadas = (data || []).map(item => ({
        id: item.id,
        nome: item.descricao, // Corrigido: usar descricao em vez de nome
        tipo: 'Bairro' // Definir tipo padrão como Bairro
      }));
      
      setLocalidades(localidadesMapeadas);
    } catch (error) {
      console.error('Erro ao carregar bairros:', error);
      toast.error('Erro ao carregar bairros');
      throw error; // Re-throw para permitir tratamento específico no componente
    }
  }, []);

  // Carregar postes (sem filtros server-side para melhor performance no mapa)
  const carregarPostes = useCallback(async (aplicarFiltros = false) => {
    if (loading) return; // Evitar múltiplas chamadas simultâneas
    
    setLoading(true);
    try {
      let query = supabase
        .from('postes_iluminacao')
        .select(`
          *,
          ccbairros (
            id,
            descricao
          )
        `)
        .order('numero_identificacao');

      // Aplicar filtros server-side apenas quando solicitado (para outros componentes)
      if (aplicarFiltros) {
        if (filtros.status) {
          query = query.eq('status', filtros.status);
        }
        if (filtros.localidade) {
          query = query.eq('localidade_id', filtros.localidade);
        }
        if (filtros.tipoLampada) {
          query = query.eq('tipo_lampada', filtros.tipoLampada);
        }
        if (filtros.busca) {
          query = query.ilike('numero_identificacao', `%${filtros.busca}%`);
        }
      }

      const { data, error } = await query;

      if (error) throw error;
      setPostes(data || []);
    } catch (error) {
      console.error('Erro ao carregar postes:', error);
      toast.error('Erro ao carregar postes de iluminação');
      throw error; // Re-throw para permitir tratamento específico no componente
    } finally {
      setLoading(false);
    }
  }, [filtros.status, filtros.localidade, filtros.tipoLampada, filtros.busca]);

  // Criar poste
  const criarPoste = async (dadosPoste) => {
    try {
      const { data, error } = await supabase
        .from('postes_iluminacao')
        .insert([dadosPoste])
        .select(`
          *,
          ccbairros (
            id,
            descricao
          )
        `);

      if (error) throw error;

      // Atualizar o estado local
      setPostes(prev => [...prev, ...data]);
      setLastUpdate(Date.now());
      
      // Forçar recarregamento para garantir sincronização
      setTimeout(() => {
        carregarPostes();
      }, 100);
      
      toast.success('Poste cadastrado com sucesso!');
      return data[0];
    } catch (error) {
      console.error('Erro ao criar poste:', error);
      toast.error('Erro ao cadastrar poste');
      throw error;
    }
  };

  // Atualizar poste
  const atualizarPoste = async (id, dadosPoste) => {
    try {
      const { data, error } = await supabase
        .from('postes_iluminacao')
        .update(dadosPoste)
        .eq('id', id)
        .select(`
          *,
          ccbairros (
            id,
            descricao
          )
        `);

      if (error) throw error;

      // Atualizar o estado local
      setPostes(prev => prev.map(poste => 
        poste.id === parseInt(id) ? data[0] : poste
      ));
      setLastUpdate(Date.now());
      
      // Forçar recarregamento para garantir sincronização
      setTimeout(() => {
        carregarPostes();
      }, 100);
      
      toast.success('Poste atualizado com sucesso!');
      return data[0];
    } catch (error) {
      console.error('Erro ao atualizar poste:', error);
      toast.error('Erro ao atualizar poste');
      throw error;
    }
  };

  // Excluir poste
  const excluirPoste = async (id) => {
    try {
      const { error } = await supabase
        .from('postes_iluminacao')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Atualizar o estado local
      setPostes(prev => prev.filter(poste => poste.id !== parseInt(id)));
      setLastUpdate(Date.now());
      
      // Forçar recarregamento para garantir sincronização
      setTimeout(() => {
        carregarPostes();
      }, 100);
      
      toast.success('Poste excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir poste:', error);
      toast.error('Erro ao excluir poste');
      throw error;
    }
  };

  // Buscar poste por ID
  const buscarPostePorId = async (id) => {
    try {
      const { data, error } = await supabase
        .from('postes_iluminacao')
        .select(`
          *,
          ccbairros (
            id,
            descricao
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao buscar poste:', error);
      toast.error('Erro ao carregar dados do poste');
      throw error;
    }
  };

  // Carregar histórico de status
  const carregarHistoricoStatus = async (posteId) => {
    try {
      const { data, error } = await supabase
        .from('historico_status_postes')
        .select('*')
        .eq('poste_id', posteId)
        .order('data_mudanca', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      toast.error('Erro ao carregar histórico de status');
      return [];
    }
  };

  // Obter estatísticas
  const obterEstatisticas = async () => {
    try {
      const { data: postesData, error } = await supabase
        .from('postes_iluminacao')
        .select('status, tipo_lampada, localidade_id');

      if (error) throw error;

      const total = postesData.length;
      const funcionando = postesData.filter(p => p.status === 'Funcionando').length;
      const alerta = postesData.filter(p => p.status === 'Alerta').length;
      const defeito = postesData.filter(p => p.status === 'Defeito').length;

      // Estatísticas por tipo de lâmpada
      const porTipoLampada = postesData.reduce((acc, poste) => {
        acc[poste.tipo_lampada] = (acc[poste.tipo_lampada] || 0) + 1;
        return acc;
      }, {});

      // Estatísticas por localidade
      const porLocalidade = postesData.reduce((acc, poste) => {
        acc[poste.localidade_id] = (acc[poste.localidade_id] || 0) + 1;
        return acc;
      }, {});

      return {
        total,
        funcionando,
        alerta,
        defeito,
        porcentagemFuncionando: total > 0 ? ((funcionando / total) * 100).toFixed(1) : 0,
        porTipoLampada,
        porLocalidade
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      return {
        total: 0,
        funcionando: 0,
        alerta: 0,
        defeito: 0,
        porcentagemFuncionando: 0,
        porTipoLampada: {},
        porLocalidade: {}
      };
    }
  };

  // Função removida - bairros são gerenciados centralmente

  // Obter postes filtrados (para componentes que precisam de filtros server-side)
  const obterPostesFiltrados = async () => {
    try {
      let query = supabase
        .from('postes_iluminacao')
        .select(`
          *,
          ccbairros (
            id,
            descricao
          )
        `)
        .order('numero_identificacao');

      // Aplicar filtros
      if (filtros.status) {
        query = query.eq('status', filtros.status);
      }
      if (filtros.localidade) {
        query = query.eq('localidade_id', filtros.localidade);
      }
      if (filtros.tipoLampada) {
        query = query.eq('tipo_lampada', filtros.tipoLampada);
      }
      if (filtros.busca) {
        query = query.ilike('numero_identificacao', `%${filtros.busca}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao obter postes filtrados:', error);
      return [];
    }
  };

  // Inicializar dados apenas uma vez
  useEffect(() => {
    if (!initialized) {
      const initializeData = async () => {
        try {
          await Promise.all([
            carregarLocalidades(),
            carregarPostes()
          ]);
          setInitialized(true);
        } catch (error) {
          console.error('Erro na inicialização:', error);
        }
      };
      
      initializeData();
    }
  }, [initialized]); // Remover dependências das funções para evitar loops

  // Função para forçar atualização dos dados
  const atualizarDados = useCallback(async () => {
    try {
      await Promise.all([
        carregarPostes(),
        carregarLocalidades()
      ]);
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
    }
  }, [carregarPostes, carregarLocalidades]);

  const value = {
    // Estados
    postes,
    localidades,
    loading,
    filtros,
    lastUpdate,

    // Ações
    setFiltros,
    carregarPostes,
    carregarLocalidades,
    criarPoste,
    atualizarPoste,
    excluirPoste,
    buscarPostePorId,
    carregarHistoricoStatus,
    obterEstatisticas,
    obterPostesFiltrados,
    atualizarDados
  };

  return (
    <PostesIluminacaoContext.Provider value={value}>
      {children}
    </PostesIluminacaoContext.Provider>
  );
}